/**
 * Quickstart Coordinator
 *
 * Orchestrates the Quickstart flow by coordinating multiple managers.
 * This class is responsible for:
 * - State management
 * - Phase transitions
 * - Manager lifecycle
 * - Public API for UI components
 *
 * Following the Coordinator pattern, this class delegates specific
 * responsibilities to focused managers while maintaining overall flow control.
 */

import { FeatureController, BaseFeatureState } from '@playcastdotio/FeatureController';
import type { DeviceHandoffMethod } from '../../telemetry/client';
import { QuickstartTheme, getQuickstartTheme, getQuickstartThemeAsync, getResolvedCtaUrl, getDeviceSupportStatus, getDeviceLockTarget, DeviceLockTarget, isIOSDevice } from './types/theme';
import {
  getHandoffTarget,
  isDeviceFallbackEnabled,
  resolveDeviceFallbackEnabledFor,
} from './types/deviceFallbackTheme';
import { EnvironmentInfo, detectEnvironment, shouldBlockQuickstart } from './utils/environmentDetection';
import { UTMParams, getUTMParams, captureUTMParams, storeUTMParams, captureDynamicParams, CapturedDynamicParams, getStoredDynamicParams } from './utils/utmCapture';
import { enrichThemeUrls } from './utils/steamUrls';
import { setupIdBridge, setPersistentPlayerId, getValidBridgeId } from './utils/idBridge';
import {
  readHandoffMethodFromUrl,
  readHandoffTokenFromUrl,
  readHandoffOriginFromUrl,
  removeHandoffParamsFromUrl,
  type HandoffOriginDevice,
} from './utils/deviceHandoff';
import { resolveHandoffToken } from '../../api/modules/deviceHandoff';
import { isDeliberateSessionEnd } from './utils/disconnectClassifier';
import { GUEST_RECONNECT_UI_TIMEOUT_MS, GUEST_RECOVERY_HARD_CEILING_MS } from '@playcastdotio/PeerCore';
import { getDefaultInputAccess, getDeniedInputAccess } from './utils/inputAccessDefaults';
import {
  shouldAutoRedirectToStore as resolveAutoRedirect,
  resolveTerminalPhase,
} from './utils/terminalRouting';
import { isMobileDevice } from '../../utility/utility';
import { playerConnectionState, playerInfoState } from '../../api/stores/playerStores';
import { authStore } from '../../api/stores/globalStores';
import { handleEvent, setDebugMessageCallback } from '../../api/modules/hostMessaging/inputEvents';
import { captureStore } from '@playcastdotio/InputCapture';
import { closePeer, rejoinGuestSession, guestRecoveryBudgetRemainingMs } from '../../api/modules/peer';
import { notifyWishlistDeadDrop } from '../../api/modules/notifications';
import { getPersistentPlayerId } from '../../api';
import { sendMessage } from '../../api/modules/realtimeMessaging';
import { initializeExitDetection, type ExitReason, type ExitMetadata, type VisibilityReason } from '../../telemetry/client';
import type { PlaycastMessage, PlaycastMessageTarget, SlugUrlConfig } from '@playcastdotio/Messaging';
import { websocket, AnalyticsMessageType } from '@playcastdotio/Messaging';
import * as Sentry from '@sentry/react';
import { getCampaignIdFromUrl, getCampaignIdFromUrlAsync, getSlugUrlConfigAsync } from '../../config/campaignUrls';
import config from '../../config.json';
import { FlowGraphRouter } from './FlowGraphRouter';
import { fetchFlowGraph } from '../../api/modules/campaigns';

// Managers
import { BrowserLockManager } from '../_base/BrowserLockManager';
import { InputCaptureManager, InputAccessRights } from '../_base/InputCaptureManager';
import type { IQuickstartTelemetryManager } from './telemetry/IQuickstartTelemetryManager';
import { QuickstartTelemetryManager } from './telemetry/QuickstartTelemetryManager';
import type { IQuickstartLiveOpsManager } from './liveops/IQuickstartLiveOpsManager';
import { QuickstartLiveOpsManager } from './liveops/QuickstartLiveOpsManager';
import { RealtimeLiveOpsTransport } from './liveops/liveOpsTransport';
import { StreamRenderedGate } from './liveops/StreamRenderedGate';
import { TimerManager } from './managers/TimerManager';
import { AuthFlowManager } from './managers/AuthFlowManager';
import { MatchmakingManager } from './managers/MatchmakingManager';
import { MatchmakingV2Dialect } from './managers/MatchmakingV2Dialect';
import { BroadcastConnectionManager } from './managers/BroadcastConnectionManager';
import { InGameIdleManager } from './managers/InGameIdleManager';

/**
 * Quickstart Flow Phases
 */
export type QuickstartPhase =
  | 'page_viewed'
  | 'environment_check'
  | 'embedded_browser_blocked'
  | 'device_not_supported'
  | 'fast_start_matchmaking'
  | 'creating_lobby'
  | 'matchmaking'
  | 'matchmaking_failed'
  | 'match_found'
  | 'joining_broadcast'
  | 'broadcast_join_failed'
  | 'match_ended'
  | 'preparing_player'
  | 'playing_afk'
  | 'playing_active'
  | 'afk_timeout'
  | 'ad_timer_expired'
  | 'wishlist_early_exit'
  | 'cta_page'
  | 'auth_attempted'
  | 'auth_failed'
  | 'thank_you'
  | 'auto_redirect'
  | 'error';

/**
 * Phases where the flow has ended and no further connection/disconnect
 * callbacks should cause state transitions. Add new end-of-flow phases here.
 */
export const TERMINAL_PHASES: ReadonlySet<QuickstartPhase> = new Set([
  'cta_page',
  'thank_you',
  'auto_redirect',
  'error',
  'auth_attempted',
  'auth_failed',
  'match_ended',
  'device_not_supported',
]);

export type QuickstartExitReason =
  | 'afk_timeout'
  | 'ad_timer_expired'
  | 'wishlist_clicked'
  | 'manual_exit'
  | 'invitation_invalidated'
  | 'loading_timeout'
  | 'matchmaking_failed'
  | 'broadcast_join_failed'
  | 'auth_failed'
  | 'device_not_supported'
  | 'match_ended'
  | 'session_ended'
  | 'connection_lost';

export interface QuickstartState extends BaseFeatureState {
  phase: QuickstartPhase;
  theme?: QuickstartTheme; // Theme loaded asynchronously from API
  themeLoaded: boolean; // Whether the theme has been resolved from API
  campaignNotFound: boolean; // Whether the campaign could not be resolved
  environment: EnvironmentInfo;
  utmParams: UTMParams;
  slugUrlConfig: SlugUrlConfig | null; // Per-slug URL configuration (overrides theme CTA/tracker)
  capturedDynamicParams: CapturedDynamicParams; // Dynamic params captured from inbound URL

  // Session identity
  sessionId: string; // Unique ID for this flow run, persists across OAuth redirects
  persistentId?: string; // Cross-session player ID, resolved by the ID bridge during init

  // Timing data (for analytics and Sentry)
  pageLoadTime?: number; // When coordinator initialized
  websocketReadyTime?: number; // When websocket auth succeeded
  lobbyCreationStartTime?: number; // When lobby creation started
  lobbyCreationDuration?: number; // Time taken to create lobby

  // Matchmaking data
  lobbyCreated: boolean;
  lobbyJoined: boolean;
  matchId?: string;
  broadcastId?: string;
  matchmakingStartTime?: number;
  matchmakingDuration?: number;
  queuePosition?: number; // Queue position from v2 QUEUE_STATUS (UI display)
  estimatedSetupTimeSeconds?: number; // Estimated setup from v2 MATCH_FOUND (UI display)
  matchCanRetry?: boolean; // v2 MATCH_FAILED canRetry flag — drives the requeue button
  matchEndReason?: string; // v2 MATCH_END reason — shown on the match-ended screen

  // Connection data
  playerConnectionId?: string;
  connectionStartTime?: number;
  connectionRetries: number; // Track reconnection attempts

  // Timer data
  afkTimeoutSeconds: number;
  adTimerSeconds: number;
  afkTimerActive: boolean;
  adTimerActive: boolean;
  sessionStartTime?: number;
  firstInputTime?: number;
  totalPlaytime: number;
  cumulativeAfkTime: number;

  // In-game idle (AFK overlay during playing_active)
  inGameIdleActive: boolean;
  inGameIdleTimeoutSeconds: number;

  // UI control
  shouldShowPlayNowOverlay: boolean;

  // Exit data
  exitReason?: QuickstartExitReason;
  shouldAutoRedirectToStore: boolean;

  // Populated when the device gate blocks entry, so the fallback screen knows
  // which variant to render (QR to phone vs. email a link to a PC).
  deviceLock?: {
    detectedDeviceType: 'ios' | 'android' | 'desktop';
    lockedTo: DeviceLockTarget;
  };

  // Auth data
  authProvider?: string;
  authAttempted: boolean;
  playerAuthId?: string;

  // Error data
  errorMessage?: string;

  // Reconnection
  isReconnecting: boolean;

  // Input access rights
  access: InputAccessRights;
}

/**
 * Quickstart Coordinator
 * Orchestrates managers to implement the complete quickstart flow
 */
export class QuickstartCoordinator extends FeatureController<QuickstartState> {
  private quickstartSlug: string;
  private frozenPersistentId: string | undefined;

  // Transport that brought this visitor here from an incompatible device, held between
  // the ID bridge setup (which reads it) and theme load (when analytics can report it).
  private arrivedViaHandoff: DeviceHandoffMethod | null = null;
  private handoffOriginDevice: HandoffOriginDevice | null = null;

  // Gates the stream-rendered funnel milestone to the first successful stream
  // per session. connectToBroadcast re-registers the peer-stream callback on
  // every reconnect, so without this a retry would emit duplicate funnel events
  // (with a stale pageLoadTime-based duration) and inflate canonical counts.
  private readonly streamRenderedGate = new StreamRenderedGate();

  // Managers
  private analyticsManager: IQuickstartTelemetryManager;
  private liveOpsManager: IQuickstartLiveOpsManager;
  private timerManager: TimerManager;
  private inGameIdleManager: InGameIdleManager;
  private authFlowManager: AuthFlowManager;
  private matchmakingManager: MatchmakingManager;
  private broadcastConnectionManager: BroadcastConnectionManager;
  private browserLockManager: BrowserLockManager;
  private inputCaptureManager: InputCaptureManager;

  // Flow graph router (null = fallback to hardcoded transitions)
  private router: FlowGraphRouter | null = null;

  // Debug callback
  private debugControllerInputCallback?: (inputType: string, eventType: string) => void;

  // Flag to track if we're intentionally disconnecting (to avoid overriding exitReason)
  private isIntentionalDisconnect = false;

  // Guard: once cleanup() runs, block all side effects (disconnect, transitions, timers)
  // so a stale coordinator instance cannot kill the shared peer connection.
  private isCleanedUp = false;

  // Flag to track if we're currently attempting a reconnect (to debounce multiple disconnect events)
  private isReconnecting = false;
  private preparingPlayerTimeout?: ReturnType<typeof setTimeout>;

  /**
   * Wall-clock instant the current recovery episode began, or null when nothing is
   * recovering. The episode runs from transport loss to a live stream or a terminal outcome
   * and spans every layer — PeerCore's reclaim ladder, its refresh and the app-level rebuilds
   * here. Bounding each layer on its own clock is what let one bad connection cost roughly
   * 33 seconds; this is the app's share of the single ten-second budget.
   */
  private recoveryEpisodeStartedAt: number | null = null;

  /**
   * The disconnect reason a pending Play-Now activation should rejoin with, or null when
   * nothing is waiting to be re-established.
   *
   * Set only by the NON-deliberate disconnect path. A deliberate end (host closed, kicked, ad
   * mode, user exit) leaves this null, so activating any affordance after one cannot start a
   * rejoin.
   */
  private pendingRejoinReason: string | null = null;

  // Timestamp of last disconnect call (for debouncing)
  private lastDisconnectTime = 0;
  private readonly DISCONNECT_DEBOUNCE_MS = 5000; // 5 second debounce
  private readonly RECONNECT_TIMEOUT_MS = GUEST_RECONNECT_UI_TIMEOUT_MS;

  // Exit detection cleanup function
  private exitDetectionCleanup?: () => void;

  // Unsubscribe from host-driven input access changes (via captureStore)
  private captureAccessUnsubscribe?: () => void;

  // Theme loading promise (must be awaited before initialization)
  private themeLoadingPromise: Promise<void>;

  constructor(quickstartSlug: string, debugPhase?: QuickstartPhase) {
    // Extract campaign ID from URL slug (e.g., 'af5ea5f1' → 'shades')
    const campaignId = getCampaignIdFromUrl(quickstartSlug);
    console.log('[QuickstartCoordinator] Campaign ID from URL:', { quickstartSlug, campaignId });

    // Generate or resume sessionId
    // ONLY resume sessionId if we're returning from OAuth redirect
    // Otherwise, always create a new sessionId (fresh quickstart flow)
    const isReturningFromOAuth = sessionStorage.getItem('quickstart_oauth_in_progress') === 'true';
    const storedSessionId = sessionStorage.getItem('quickstart_session_id');

    let sessionId: string;
    if (isReturningFromOAuth && storedSessionId) {
      // Resuming after OAuth redirect - keep same sessionId
      sessionId = storedSessionId;
      console.log('[QuickstartCoordinator] Resuming session after OAuth redirect:', sessionId);
    } else {
      // Fresh page load or refresh - create new sessionId
      sessionId = `session_${crypto.randomUUID()}`;
      console.log('[QuickstartCoordinator] New session started:', sessionId);

      // Clean up any stale sessionId from previous flows
      if (storedSessionId) {
        sessionStorage.removeItem('quickstart_session_id');
        console.log('[QuickstartCoordinator] Cleared stale sessionId from previous flow');
      }
    }

    const environment = detectEnvironment();
    const utmParams = getUTMParams();

    console.log('[QuickstartCoordinator] UTM params captured:', process.env.NODE_ENV !== 'production' ? utmParams : Object.keys(utmParams).filter(k => utmParams[k as keyof typeof utmParams]));

    const initialPhase = debugPhase || 'page_viewed';

    super({
      status: 'idle',
      phase: initialPhase,
      sessionId,
      theme: undefined, // Theme will be loaded asynchronously
      themeLoaded: false, // Theme loading will happen async
      campaignNotFound: false,
      environment,
      utmParams,
      slugUrlConfig: null, // Loaded async after slug resolution
      capturedDynamicParams: {}, // Populated after slugUrlConfig loads
      pageLoadTime: Date.now(), // Track page load time
      lobbyCreated: false,
      lobbyJoined: false,
      matchId: debugPhase ? 'debug-match-123' : undefined,
      broadcastId: debugPhase ? 'debug-broadcast-456' : undefined,
      playerConnectionId: debugPhase ? 'debug-player-789' : undefined,
      connectionRetries: 0,
      afkTimeoutSeconds: 180, // Default 3 minutes until theme loads
      adTimerSeconds: 600, // Default 10 minutes until theme loads
      afkTimerActive: debugPhase === 'playing_afk',
      adTimerActive: debugPhase === 'playing_active',
      inGameIdleActive: false,
      inGameIdleTimeoutSeconds: 0, // Disabled until theme loads
      shouldShowPlayNowOverlay: true,
      sessionStartTime: debugPhase === 'playing_afk' || debugPhase === 'playing_active' ? Date.now() : undefined,
      firstInputTime: debugPhase === 'playing_active' ? Date.now() : undefined,
      totalPlaytime: debugPhase ? 120000 : 0,
      cumulativeAfkTime: 0,
      authAttempted: false,
      exitReason: debugPhase === 'cta_page' ? 'ad_timer_expired' : undefined,
      shouldAutoRedirectToStore: false,
      isReconnecting: false,
      errorMessage: debugPhase === 'matchmaking_failed' || debugPhase === 'broadcast_join_failed' ? 'Debug error message' : undefined,
      access: QuickstartCoordinator.getInputAccessRights(!!debugPhase),
    });

    this.quickstartSlug = quickstartSlug;

    // CRITICAL: Store theme loading promise so we can await it in onInit()
    // This ensures theme is fully loaded before any checks or analytics
    // Resolve campaign ID from API and load theme asynchronously
    // In debug mode, fall back to test theme on failure instead of showing 404
    this.themeLoadingPromise = this.resolveCampaignFromAPI(quickstartSlug, campaignId, !!debugPhase);

    // Initialize managers
    this.analyticsManager = new QuickstartTelemetryManager({
      getState: () => this.state.currentState,
    });

    // Dedicated LiveOps funnel emitter — trusted channel, parallel to (never
    // replacing) the analytics milestone calls. Server stamps identity.
    this.liveOpsManager = new QuickstartLiveOpsManager(new RealtimeLiveOpsTransport());

    this.timerManager = new TimerManager({
      getState: () => this.state.currentState,
      updateState: (state) => this.state.updateState({ ...this.state.currentState, ...state }),
      onAFKTimeout: () => this.onAfkTimeout(),
      onAdTimeout: () => this.onAdTimerExpired(),
    });

    this.inGameIdleManager = new InGameIdleManager({
      timeoutSeconds: 0, // Updated when theme loads
      onIdleStart: () => {
        this.state.updateState({ ...this.state.currentState, inGameIdleActive: true });
      },
      onIdleEnd: () => {
        this.state.updateState({ ...this.state.currentState, inGameIdleActive: false });
      },
      onIdleTimeout: () => {
        this.handleIntentionalExit('afk_timeout', 'afk_timeout');
      },
    });

    this.authFlowManager = new AuthFlowManager({
      getState: () => this.state.currentState,
      updateState: (state) => this.state.updateState({ ...this.state.currentState, ...state }),
      transitionTo: (phase) => this.transitionTo(phase as QuickstartPhase),
      transitionToThankYou: () => this.transitionToThankYou(),
      sendAuthAttempted: () => this.analyticsManager.sendAuthAttempted(),
      sendAuthFailed: (provider, reason) => this.analyticsManager.sendAuthFailed(provider, reason),
      sendAuthSucceeded: (provider, playerAuthId, attemptId, accountState) =>
        this.analyticsManager.sendAuthSucceeded(provider, playerAuthId, attemptId, accountState),
      sendThankYouPageViewed: (playerAuthId) => this.analyticsManager.sendThankYouPageViewed(playerAuthId),
      sendWishlistEmail: (playerAuthId, email, persistentId) => this.onWishlistEmailSend(playerAuthId, email, persistentId),
    });

    this.matchmakingManager = new MatchmakingManager({
      getState: () => this.state.currentState,
      updateState: (state) => this.state.updateState({ ...this.state.currentState, ...state }),
      transitionTo: (phase) => this.transitionTo(phase as QuickstartPhase),
      onMatchmakingFailed: (reason) => this.onMatchmakingFailed(reason),
      onBroadcastReady: (broadcastId) => this.broadcastConnectionManager.connectToBroadcast(),
      onInvitationInvalidated: () => this.onInvitationInvalidated(),
      onMatchEnded: (reason) => this.onMatchEnded(reason),
      sendWebSocketConnectionStarted: () => this.analyticsManager.sendWebSocketConnectionStarted(true),
      sendLobbyCreationStarted: () => this.analyticsManager.sendLobbyCreationStarted(),
      sendMatchmakingStarted: (timeout) => this.analyticsManager.sendMatchmakingStarted(timeout),
      sendMatchmakingSucceeded: () => this.analyticsManager.sendMatchmakingSucceeded(),
      logSlowSequence: (name, duration, context) => this.logSlowSequenceToSentry(name, duration, context),
      failOnNoHostsAvailable: config.FAIL_ON_NO_HOSTS_AVAILABLE ?? true, // Default to true
      // Campaign ID now read from state.theme.slug when matchmaking starts
    });

    this.broadcastConnectionManager = new BroadcastConnectionManager({
      getState: () => this.state.currentState,
      updateState: (state) => this.state.updateState({ ...this.state.currentState, ...state }),
      transitionTo: (phase) => this.transitionTo(phase as QuickstartPhase),
      onConnected: () => this.onBroadcastConnected(),
      onConnectionFailed: (reason) => this.onBroadcastJoinFailed(reason),
      onDisconnected: (kickReason) => this.onPlayerDisconnect(),
      onReconnecting: (_kickReason) => {
        if (this.isIntentionalDisconnect || this.isTerminalPhase()) return;
        // The episode starts at transport loss, not at the first app-level rebuild — every
        // second PeerCore spends reclaiming is a second the player is already waiting.
        this.beginRecoveryEpisode();
        this.isReconnecting = true;
        this.state.updateState({ ...this.state.currentState, isReconnecting: true });
      },
      onReconnectRecovered: () => {
        if (this.isIntentionalDisconnect || this.isTerminalPhase()) return;
        this.endRecoveryEpisode();
        this.isReconnecting = false;
        this.state.updateState({ ...this.state.currentState, isReconnecting: false });
      },
      sendBroadcastJoinAttempted: () => this.analyticsManager.sendBroadcastJoinAttempted(),
      sendBroadcastJoinFailed: (reason, duration, errorStage) =>
        this.analyticsManager.sendBroadcastJoinFailed(reason, duration, errorStage),
      sendBroadcastJoinSucceeded: (duration) => this.analyticsManager.sendBroadcastJoinSucceeded(duration),
      sendPeerIdAcquired: (peerId) => this.analyticsManager.sendPeerIdAcquired(peerId),
      sendSdpOfferSent: () => this.analyticsManager.sendSdpOfferSent(),
      sendSdpAnswerReceived: () => this.analyticsManager.sendSdpAnswerReceived(),
      sendIceCandidatesSent: (count) => this.analyticsManager.sendIceCandidatesSent(count),
      sendIceCheckingStarted: () => this.analyticsManager.sendIceCheckingStarted(),
      sendIceConnected: () => this.analyticsManager.sendIceConnected(),
      sendRemoteStreamReceived: () => {
        // Raw technical analytics event fires on every peer stream (incl. reconnects).
        this.analyticsManager.sendRemoteStreamReceived();
        // The stream_rendered funnel milestone is a once-per-session conversion
        // signal; guard it so a reconnect can't double-count with a stale duration.
        if (this.streamRenderedGate.shouldEmit()) {
          this.analyticsManager.sendMilestoneStreamRendered();
          this.liveOpsManager.emitStreamRendered(
            this.state.currentState.pageLoadTime ? Date.now() - this.state.currentState.pageLoadTime : undefined,
          );
        }
      },
      logSlowSequence: (name, duration, context) => this.logSlowSequenceToSentry(name, duration, context),
    });

    this.browserLockManager = new BrowserLockManager({
      onFullscreenChange: (isFullscreen) => this.handleBrowserLockChange('fullscreen', isFullscreen),
      onPointerLockChange: (isLocked) => this.handleBrowserLockChange('pointerLock', isLocked),
      onVisibilityChange: (isVisible) => this.handleBrowserLockChange('visibility', isVisible),
    });

    this.inputCaptureManager = new InputCaptureManager({
      access: this.state.currentState.access,
      onKeyboard: (event) => handleEvent(event),
      onMouse: (event) => handleEvent(event),
      onTouch: (event) => handleEvent(event),
      onGamepad: (gamepads) => {
        const event = new CustomEvent('gamepad', { detail: gamepads });
        handleEvent(event);
      },
      onAnyInput: () => this.inGameIdleManager.resetIdleTimer(),
      onDebugCapture: (inputType, eventType) => this.debugControllerInputCallback?.(inputType, eventType),
    });

    // `captureStore` is a module-level singleton shared across coordinator
    // instances and only reset on a full page reload. A prior /q/ session can
    // leave a stale grant in it, so reset it to this session's baseline at
    // construction. Live sessions start fully denied and wait for the host's
    // `setInputAccess`; debug sessions have no host, so they seed the permissive
    // default instead.
    const baselineAccess = debugPhase ? getDefaultInputAccess() : getDeniedInputAccess();
    captureStore.updateAccess(baselineAccess);
    // Clear any prior session's host-grant flag so this session starts from a
    // clean slate (captureStore is a page-lifetime singleton).
    captureStore.resetHostAccess();

    // The host delivers the player's authoritative input access via
    // `setInputAccess` after connect, which lands in `captureStore`. Subscribe so
    // we re-bind capture listeners when the host grants or revokes an input
    // (notably touch for touch-native campaigns). captureStore is the single
    // source of truth once a host message arrives.
    this.captureAccessUnsubscribe = captureStore.subscribeAccess((access) => this.applyHostInputAccess(access));

    // `subscribeAccess` does not replay the current value, and `updateAccess`
    // only notifies on change, so a host grant that equals the baseline (or that
    // arrives before this subscription) would never reach the coordinator,
    // leaving `state.access` / `InputCaptureManager` on their constructor
    // snapshot. Reconcile once against the store now so all three sources agree
    // regardless of de-dupe or ordering (#13843).
    this.applyHostInputAccess(captureStore.getAccess());
  }

  /**
   * Apply a host-driven input access update (from `captureStore`) to both the
   * coordinator state and the live `InputCaptureManager`. Unspecified fields fall
   * back to the current access so a partial update never silently disables an
   * input.
   */
  private applyHostInputAccess(access: InputAccessRights): void {
    if (this.isCleanedUp) return;

    const current = this.state.currentState.access;
    const next: InputAccessRights = {
      mouse: access.mouse ?? current.mouse,
      keyboard: access.keyboard ?? current.keyboard,
      touch: access.touch ?? current.touch,
      gamepad: access.gamepad ?? current.gamepad,
    };

    this.state.updateState({ ...this.state.currentState, access: next });
    this.inputCaptureManager.updateAccess(next);
  }

  /**
   * Resolve campaign ID from API and load theme
   *
   * Uses API to resolve URL slug to campaign ID (checks Redis for admin-configured mappings),
   * then loads the theme for that campaign.
   * Falls back to hardcoded mappings if API resolution fails.
   *
   * @param isDebugMode - If true, falls back to "test" theme on failure instead of showing 404
   */
  private async resolveCampaignFromAPI(urlSlug: string, fallbackCampaignId: string | null, isDebugMode = false): Promise<void> {
    try {
      // Resolve slug from API (Redis-backed) and fetch slug URL config in parallel
      const [apiCampaignId, slugUrlConfig] = await Promise.all([
        getCampaignIdFromUrlAsync(urlSlug),
        getSlugUrlConfigAsync(urlSlug),
      ]);

      // Store slug URL config and capture dynamic params if config exists
      if (slugUrlConfig) {
        const baseSlug = urlSlug.split('~')[0];
        const capturedParams = captureDynamicParams(slugUrlConfig.dynamicInboundParams, baseSlug);
        this.state.updateState({
          ...this.state.currentState,
          slugUrlConfig,
          capturedDynamicParams: capturedParams,
        });
        if (process.env.NODE_ENV !== 'production') {
          console.log('[QuickstartCoordinator] Slug URL config loaded, dynamic params captured:', capturedParams);
        } else {
          console.log('[QuickstartCoordinator] Slug URL config loaded, dynamic params captured:', Object.keys(capturedParams));
        }
      }

      // Use API result if available, otherwise use fallback from constructor
      const campaignId = apiCampaignId || fallbackCampaignId;

      // Load theme for resolved campaign, passing the slug config we already
      // fetched so enrichment is deterministic (not racing currentState).
      await this.loadThemeFromAPI(campaignId, isDebugMode, slugUrlConfig);
    } catch (error) {
      console.error('[QuickstartCoordinator] Failed to resolve campaign from API:', error);
      // Load theme with fallback campaign ID
      await this.loadThemeFromAPI(fallbackCampaignId, isDebugMode);
    }
  }

  /**
   * Load theme from API asynchronously
   * Sets campaignNotFound flag if theme resolution fails (unless in debug mode)
   *
   * @param isDebugMode - If true, falls back to "test" theme on failure instead of showing 404
   * @param slugUrlConfig - Pre-fetched slug URL config; applied to the theme at load
   */
  private async loadThemeFromAPI(
    campaignId: string | null,
    isDebugMode = false,
    slugUrlConfig?: SlugUrlConfig | null,
  ): Promise<void> {
    try {
      const theme = await getQuickstartThemeAsync(campaignId);

      // If theme not found
      if (!theme) {
        console.error('[QuickstartCoordinator] Theme not found for campaign:', campaignId);

        // In debug mode, fall back to test theme
        if (isDebugMode) {
          console.log('[QuickstartCoordinator] Debug mode: falling back to test theme');
          const testTheme = getQuickstartTheme('test');
          const idleTimeout = testTheme.inGameIdleTimeoutSeconds ?? 60;
          this.state.updateState({
            ...this.state.currentState,
            theme: testTheme,
            themeLoaded: true,
            campaignNotFound: false,
            afkTimeoutSeconds: testTheme.afkTimeoutSeconds,
            adTimerSeconds: testTheme.adTimerSeconds,
            inGameIdleTimeoutSeconds: idleTimeout,
          });
          this.applyIdleTimeout(idleTimeout);
          return;
        }

        // Normal mode: show not found page
        this.state.updateState({
          ...this.state.currentState,
          themeLoaded: true,
          campaignNotFound: true,
        });
        return;
      }

      const idleTimeout = theme.inGameIdleTimeoutSeconds ?? 60;

      // Enrich theme URLs with the pre-fetched slug config (deterministic).
      const enrichedTheme = this.enrichThemeWithSlugConfig(theme, slugUrlConfig);

      // Update state with API-fetched theme
      this.state.updateState({
        ...this.state.currentState,
        theme: enrichedTheme,
        themeLoaded: true,
        campaignNotFound: false,
        afkTimeoutSeconds: theme.afkTimeoutSeconds,
        adTimerSeconds: theme.adTimerSeconds,
        inGameIdleTimeoutSeconds: idleTimeout,
      });

      this.applyIdleTimeout(idleTimeout);
      this.applyMatchmakingDialect(theme.matchmakingVersion);
      console.log('[QuickstartCoordinator] Theme loaded from API:', theme.slug);
    } catch (error) {
      console.error('[QuickstartCoordinator] Failed to load theme from API:', error);

      // In debug mode, fall back to test theme
      if (isDebugMode) {
        console.log('[QuickstartCoordinator] Debug mode: falling back to test theme after error');
        const testTheme = getQuickstartTheme('test');
        const idleTimeout = testTheme.inGameIdleTimeoutSeconds ?? 60;
        this.state.updateState({
          ...this.state.currentState,
          theme: testTheme,
          themeLoaded: true,
          campaignNotFound: false,
          afkTimeoutSeconds: testTheme.afkTimeoutSeconds,
          adTimerSeconds: testTheme.adTimerSeconds,
          inGameIdleTimeoutSeconds: idleTimeout,
        });
        this.applyIdleTimeout(idleTimeout);
        return;
      }

      // Normal mode: show not found page
      this.state.updateState({
        ...this.state.currentState,
        themeLoaded: true,
        campaignNotFound: true,
      });
    }
  }

  private applyMatchmakingDialect(version?: 'v1' | 'v2'): void {
    // Absent/undefined preserves v1 (schema + matchmaking-v2.md Phase 6 default).
    const effectiveVersion = resolveMatchmakingDialectVersion(version);
    if (effectiveVersion === 'v2') {
      this.matchmakingManager.setDialect(new MatchmakingV2Dialect());
      console.log('[QuickstartCoordinator] Using matchmaking v2 dialect');
    } else {
      console.log('[QuickstartCoordinator] Using matchmaking v1 dialect');
    }
  }

  /**
   * Enrich theme URLs with slug-config URLs + pass-through params.
   * Overwrites the theme's CTA/tracker fields so all existing call sites
   * (getResolvedCtaUrl, etc.) automatically use the enriched URLs.
   *
   * The config is passed in (rather than read from currentState) so the
   * theme is enriched deterministically even when the state update from
   * resolveCampaignFromAPI hasn't committed yet.
   */
  private enrichThemeWithSlugConfig(
    theme: QuickstartTheme,
    slugUrlConfig: SlugUrlConfig | null | undefined,
  ): QuickstartTheme {
    if (!slugUrlConfig) return theme;

    const { capturedDynamicParams } = this.state.currentState;
    const baseSlug = this.quickstartSlug.split('~')[0];
    const params = Object.keys(capturedDynamicParams).length > 0
      ? capturedDynamicParams
      : getStoredDynamicParams(baseSlug);

    const enrichedUrls = enrichThemeUrls(theme, slugUrlConfig, params, isIOSDevice());
    return { ...theme, ...enrichedUrls };
  }

  /**
   * Track websocket ready timing
   * Listens for when authStore becomes loaded (after AUTH_SUCCESS)
   */
  private trackWebsocketReadyTime(): void {
    // Check if already loaded
    if (authStore.currentState.loaded) {
      const websocketReadyTime = Date.now();
      this.state.updateState({ ...this.state.currentState, websocketReadyTime });
      console.log('[QuickstartCoordinator] Websocket already ready');

      this.analyticsManager.sendWebSocketReady();

      // Send to Sentry if > 3 seconds
      const totalDuration = this.state.currentState.pageLoadTime ? websocketReadyTime - this.state.currentState.pageLoadTime : undefined;
      this.logSlowSequenceToSentry('websocket_ready', totalDuration ?? 0, { totalDuration });

      // Send initial exit payload to API now that websocket is ready
      this.sendExitPayloadToAPI();
      return;
    }

    // Otherwise, listen for when it becomes loaded
    const listenerId = authStore.addListener((state) => {
      if (state.loaded && !this.state.currentState.websocketReadyTime) {
        const websocketReadyTime = Date.now();
        this.state.updateState({ ...this.state.currentState, websocketReadyTime });
        console.log('[QuickstartCoordinator] Websocket ready');

        this.analyticsManager.sendWebSocketReady();

        // Send to Sentry if > 3 seconds
        const totalDuration = this.state.currentState.pageLoadTime ? websocketReadyTime - this.state.currentState.pageLoadTime : undefined;
        this.logSlowSequenceToSentry('websocket_ready', totalDuration ?? 0, { totalDuration });

        // Send initial exit payload to API now that websocket is ready
        this.sendExitPayloadToAPI();

        authStore.removeListener(listenerId);
      }
    });
  }

  /**
   * Log slow sequences to Sentry (informational level)
   */
  private logSlowSequenceToSentry(sequenceName: string, duration: number, context?: Record<string, unknown>): void {
    const THRESHOLD_MS = 3000; // 3 seconds

    if (duration > THRESHOLD_MS) {
      const state = this.state.currentState;

      // Only log if Sentry is initialized
      if (Sentry.isInitialized()) {
        Sentry.captureMessage(`Slow ${sequenceName} sequence: ${duration}ms`, {
          level: 'info',
          tags: {
            sequence: sequenceName,
            quickstart_slug: state.theme?.slug || 'unknown',
            phase: state.phase,
          },
          contexts: {
            timing: {
              duration_ms: duration,
              threshold_ms: THRESHOLD_MS,
              ...(context || {}),
            },
            quickstart: {
              phase: state.phase,
              theme_slug: state.theme?.slug || 'unknown',
              player_auth_id: state.playerAuthId,
              broadcast_id: state.broadcastId,
              match_id: state.matchId,
            },
          },
        });

        console.warn(`[QuickstartCoordinator] Slow ${sequenceName}: ${duration}ms (threshold: ${THRESHOLD_MS}ms)`);
      }
    }
  }

  /**
   * Get input access rights
   */
  private static getInputAccessRights(isDebugMode: boolean): InputAccessRights {
    // Debug mode has no host to grant access, so keep the permissive snapshot so
    // the debug harness can exercise every input.
    if (isDebugMode) {
      return getDefaultInputAccess();
    }

    const peerId = playerConnectionState.currentState.playerPeerId;
    if (peerId) {
      const playerInfo = playerInfoState.currentState.players[peerId];
      if (playerInfo?.access) {
        return {
          mouse: playerInfo.access.mouse,
          keyboard: playerInfo.access.keyboard,
          gamepad: playerInfo.access.gamepad,
          touch: playerInfo.access.touch,
        };
      }
    }

    // Fail closed for live sessions: start with everything denied and let the
    // host's `setInputAccess` (bridged through `captureStore`) grant access. This
    // removes the permissive window that leaked touch and the mobile controller
    // when the host restricted inputs (#13843).
    return getDeniedInputAccess();
  }

  /**
   * Initialize the coordinator
   */
  protected async onInit(): Promise<void> {
    console.log('[QuickstartCoordinator] Initializing...');

    // ===== ID BRIDGE SETUP (CRITICAL: BEFORE ANALYTICS) =====
    const isEmbedded = this.state.currentState.environment.isEmbedded;
    const isMobile = isMobileDevice();

    // Read the handoff transport before the ID bridge cleans the URL, and seed storage
    // from an emailed token first — its persid/persts pair would have long expired, so
    // the bridge alone would treat this arrival as a brand-new visitor.
    this.arrivedViaHandoff = readHandoffMethodFromUrl();
    this.handoffOriginDevice = readHandoffOriginFromUrl();
    const handoffToken = readHandoffTokenFromUrl();

    // The transport / origin params are only claims. A QR left on screen outlives its
    // bridge by BRIDGE_EXPIRY_MS, so a late scan arrives with `pchandoff`/`pchorig` intact
    // but no identity to carry — and a URL can be shared with those params by hand.
    // Closing the funnel on the claim alone would attribute a brand-new visitor to a
    // handoff that never happened (and let a fabricated pchorig colour that fiction), so
    // completion is reported only when an identity actually crossed over.
    let identityCarriedOver = false;
    if (handoffToken) {
      const bridgedId = await resolveHandoffToken(handoffToken);
      if (bridgedId) {
        setPersistentPlayerId(bridgedId);
        identityCarriedOver = true;
      }
    } else if (getValidBridgeId()) {
      // Read before setupIdBridge consumes and clears the pair.
      identityCarriedOver = true;
    }

    if (!identityCarriedOver) {
      this.arrivedViaHandoff = null;
      this.handoffOriginDevice = null;
    }

    const persistentId = setupIdBridge(isEmbedded, isMobile);
    removeHandoffParamsFromUrl();
    this.frozenPersistentId = persistentId;
    this.analyticsManager.setPersistentId(persistentId);
    this.state.updateState({ ...this.state.currentState, persistentId });
    console.log('[QuickstartCoordinator] PersistentId established:', persistentId);
    console.log('[QuickstartCoordinator] Embedded browser:', isEmbedded, 'Mobile device:', isMobile);
    console.log('[QuickstartCoordinator] Current URL:', window.location.href);
    // ===== END ID BRIDGE SETUP =====

    // If starting in debug mode with a playing phase, start the appropriate timer
    const currentPhase = this.state.currentState.phase;
    if (currentPhase === 'playing_afk') {
      console.log('[QuickstartCoordinator] Debug mode: Starting AFK timer for initial playing_afk phase');
      this.timerManager.startAFKTimer();
      this.browserLockManager.init();
    } else if (currentPhase === 'playing_active') {
      console.log('[QuickstartCoordinator] Debug mode: Starting Ad timer for initial playing_active phase');
      this.timerManager.startAdTimer();
      this.browserLockManager.init();
      this.inputCaptureManager.start();
    }

    // Capture and store UTM params
    captureUTMParams();
    storeUTMParams(this.state.currentState.utmParams);

    // Use URL slug directly for storage keys in coordinator (theme might not be loaded yet)
    const campaignSlug = this.quickstartSlug;
    console.log('[QuickstartCoordinator] Using campaign slug for storage keys:', campaignSlug, '(URL slug)');

    // CRITICAL: Wait for theme to load before proceeding
    // This ensures we have all data needed for analytics and checks
    console.log('[QuickstartCoordinator] Waiting for theme to load...');
    await this.themeLoadingPromise;
    console.log('[QuickstartCoordinator] Theme loaded, proceeding with initialization');

    // Verify theme is loaded (safety check)
    if (!this.state.currentState.theme) {
      console.error('[QuickstartCoordinator] Theme failed to load, cannot proceed');
      // campaignNotFound flag will be set by loadThemeFromAPI
      return;
    }

    // Track websocket ready timing (must be after theme load so analytics have theme data)
    this.trackWebsocketReadyTime();

    // Initialize exit detection early so it's active for all paths including OAuth
    this.initializeExitDetection();

    // Check if we're returning from an OAuth redirect
    // The auth component sets this flag right before redirecting to OAuth
    // We check it once and immediately clear it to avoid auth loop on refresh
    const isReturningFromOAuth = sessionStorage.getItem('quickstart_oauth_in_progress') === 'true';

    // Send Session Start analytics (only on initial page load, not OAuth returns)
    // This fires before environment check and before any auth events
    if (!isReturningFromOAuth) {
      console.log('[QuickstartCoordinator] Sending Session Start analytics');
      this.analyticsManager.sendSessionStart();
      this.analyticsManager.sendMilestonePageLoaded();
      this.liveOpsManager.emitPageLoaded();
      // New session: allow the stream-rendered milestone to fire once again.
      this.streamRenderedGate.reset();

      // Closes the cross-device funnel. Deferred to here rather than fired during the
      // bridge setup above because analytics needs the loaded theme for its base props.
      //
      // Session Start deliberately runs ahead of the environment check, so support has to
      // be read directly: a handoff that lands on a second incompatible device is about to
      // hit the fallback again, and counting that as a completion would overstate exactly
      // the metric this funnel exists to measure.
      if (this.arrivedViaHandoff) {
        const { deviceType, supported } = getDeviceSupportStatus(this.state.currentState.theme);
        if (supported) {
          this.analyticsManager.sendDeviceHandoffCompleted(
            this.arrivedViaHandoff,
            deviceType,
            this.handoffOriginDevice ?? undefined,
          );
        }
        this.arrivedViaHandoff = null;
        this.handoffOriginDevice = null;
      }
    } else {
      console.log('[QuickstartCoordinator] Skipping Session Start (OAuth return, sessionId already exists)');
    }

    console.log('[QuickstartCoordinator] OAuth detection:', {
      isReturningFromOAuth,
      playerAuthId: authStore.currentState.playerAuthId
    });

    if (isReturningFromOAuth) {
      // Clear flag immediately to prevent triggering on subsequent refreshes
      sessionStorage.removeItem('quickstart_oauth_in_progress');
      console.log('[QuickstartCoordinator] Cleared OAuth in-progress flag');

      const authId = authStore.currentState.playerAuthId;

      this.analyticsManager.sendOAuthReturnDetected(!!authId);

      if (authId) {
        console.log('[QuickstartCoordinator] Detected OAuth return with authenticated user, going to thank you page');

        // Update playerAuthId in state before transitioning
        this.state.updateState({
          ...this.state.currentState,
          playerAuthId: authId,
        });

        // Use transitionToThankYou helper to properly handle auto-redirect
        this.transitionToThankYou();
        return;
      } else {
        console.log('[QuickstartCoordinator] OAuth return detected but authId not loaded yet, going to auth page to wait');

        // Auth is in progress but authStore hasn't loaded yet
        // Go to auth_attempted phase and let the auth component handle it
        this.transitionTo('auth_attempted');
        return;
      }
    }

    // Fetch the saved flow graph and build the router
    const campaignId = this.state.currentState.theme?.slug;
    if (campaignId) {
      try {
        const graph = await fetchFlowGraph(campaignId);
        if (graph?.nodes?.length) {
          this.router = new FlowGraphRouter(graph);
          console.log('[QuickstartCoordinator] Flow graph router initialized for campaign:', campaignId);
        }
      } catch (err) {
        console.warn('[QuickstartCoordinator] Failed to load flow graph, using hardcoded transitions:', err);
      }
    }

    // Start environment check
    await this.checkEnvironment();
  }

  /**
   * Transition to a new phase
   */
  private transitionTo(phase: QuickstartPhase): void {
    if (this.isCleanedUp) {
      console.warn('[QuickstartCoordinator] Ignoring transitionTo after cleanup:', phase);
      return;
    }
    const previousPhase = this.state.currentState.phase;
    if (phase === previousPhase) return;

    const sessionIdPrefix = this.state.currentState.sessionId.slice(8, 16); // Get 8 chars after "session_"
    console.log(`[QuickstartCoordinator:${sessionIdPrefix}] ${previousPhase} → ${phase}`);

    this.state.updateState({ ...this.state.currentState, phase });

    // Initialize browser lock manager when entering playing phases
    const isEnteringPlayingPhase =
      (phase === 'playing_afk' || phase === 'playing_active') && previousPhase !== 'playing_afk' && previousPhase !== 'playing_active';
    if (isEnteringPlayingPhase) {
      console.log('[QuickstartCoordinator] Entering playing phase - initializing browser lock manager');
      this.browserLockManager.init();

      // Start appropriate timer when entering playing phase (only if not already started)
      // This handles debug mode (where onBroadcastConnected is bypassed)
      // Normal flow starts timers explicitly in onFirstInput/onReturnToPlayNow to avoid race conditions
      const hasAFKTimer = this.state.currentState.afkTimerActive;
      const hasAdTimer = this.state.currentState.adTimerActive;

      if (phase === 'playing_afk' && !hasAFKTimer) {
        console.log('[QuickstartCoordinator] Starting AFK timer on phase entry (timer not yet started)');
        this.timerManager.startAFKTimer();
      } else if (phase === 'playing_active' && !hasAdTimer) {
        console.log('[QuickstartCoordinator] Starting Ad timer on phase entry (timer not yet started)');
        this.timerManager.startAdTimer();
      }
    }

    // Release browser locks when transitioning to exit/CTA pages
    const exitPhases: QuickstartPhase[] = ['afk_timeout', 'ad_timer_expired', 'wishlist_early_exit', 'cta_page', 'match_ended'];
    const isPlayingPhase = previousPhase === 'playing_afk' || previousPhase === 'playing_active';
    const isExitPhase = exitPhases.includes(phase);

    if (isPlayingPhase && isExitPhase) {
      console.log('[QuickstartCoordinator] Transitioning to exit page - releasing browser locks');
      // Release locks asynchronously (don't await to avoid blocking transition)
      this.browserLockManager.releaseAllLocks().catch((err) => {
        console.error('[QuickstartCoordinator] Failed to release locks:', err);
      });
    }

    // Handle input capture and in-game idle based on phase
    const isEnteringActive = phase === 'playing_active';
    const isLeavingActive = previousPhase === 'playing_active' && phase !== 'playing_active';

    if (isEnteringActive) {
      this.inputCaptureManager.start();
      this.inGameIdleManager.start();
    } else if (isLeavingActive) {
      this.inputCaptureManager.stop();
      this.inGameIdleManager.stop();
    }

    // Handle cumulative AFK time
    if (previousPhase === 'playing_afk' && phase === 'playing_active') {
      const afkTime = this.state.currentState.sessionStartTime
        ? this.timerManager.calculateAFKTime(this.state.currentState.sessionStartTime)
        : 0;
      const newCumulative = this.state.currentState.cumulativeAfkTime + afkTime;
      this.state.updateState({ ...this.state.currentState, cumulativeAfkTime: newCumulative });
    }

    // Update exit payload with new phase
    this.sendExitPayloadToAPI();
  }

  /**
   * Follow a flow graph edge from the current phase.
   * Returns true if a valid edge was found and the transition was made.
   * Returns false if no router or no matching edge (caller should fall back to hardcoded behavior).
   */
  private followEdge(outputPin: string): boolean {
    if (!this.router) return false;

    const currentPhase = this.state.currentState.phase;
    const nextPhase = this.router.getNextPhase(currentPhase, outputPin);

    if (!nextPhase) {
      console.log(`[QuickstartCoordinator] No graph edge: ${currentPhase} -[${outputPin}]-> ???`);
      return false;
    }

    console.log(`[QuickstartCoordinator] Graph edge: ${currentPhase} -[${outputPin}]-> ${nextPhase}`);
    this.transitionTo(nextPhase as QuickstartPhase);
    this.onPhaseEntered(nextPhase as QuickstartPhase);
    return true;
  }

  /**
   * Triggers the appropriate side effect when entering a phase via graph routing.
   */
  private onPhaseEntered(phase: QuickstartPhase): void {
    switch (phase) {
      case 'environment_check':
        this.runEnvironmentCheckRouted();
        break;
      case 'fast_start_matchmaking':
        this.matchmakingManager.startFastStartMatchmaking();
        break;
      case 'creating_lobby':
        if (config.USE_FAST_START) {
          this.matchmakingManager.startFastStartMatchmaking();
        } else {
          this.matchmakingManager.startMatchmaking();
        }
        break;
      case 'cta_page': {
        const totalSessionDuration = this.state.currentState.sessionStartTime
          ? Date.now() - this.state.currentState.sessionStartTime : 0;
        this.analyticsManager.sendCTAPageViewed(totalSessionDuration);
        break;
      }
      case 'thank_you':
        this.analyticsManager.sendThankYouPageViewed(
          this.state.currentState.playerAuthId || 'anonymous'
        );
        break;
      case 'auto_redirect': {
        const resolvedCtaForRedirect = this.state.currentState.theme ? getResolvedCtaUrl(this.state.currentState.theme) : undefined;
        if (!resolvedCtaForRedirect) {
          if (!this.followEdge('failure')) {
            this.transitionTo('thank_you');
            this.analyticsManager.sendThankYouPageViewed(
              this.state.currentState.playerAuthId || 'anonymous'
            );
          }
          break;
        }
        if (!this.state.currentState.shouldAutoRedirectToStore) {
          this.state.updateState({
            ...this.state.currentState,
            shouldAutoRedirectToStore: true,
          });
        }
        break;
      }
      case 'preparing_player':
        break;
      default:
        break;
    }
  }

  /**
   * Environment check logic used by the graph router.
   */
  private runEnvironmentCheckRouted(): void {
    this.analyticsManager.sendEnvironmentCheckCompleted();
    const shouldBlock = shouldBlockQuickstart(this.state.currentState.environment);

    if (shouldBlock) {
      if (!this.followEdge('embedded')) {
        this.transitionTo('embedded_browser_blocked');
        this.analyticsManager.sendEmbeddedBrowserBlocked();
      }
    } else {
      const devicePin = isMobileDevice() ? 'mobile' : 'desktop';
      if (!this.followEdge(devicePin)) {
        if (config.USE_FAST_START) {
          this.matchmakingManager.startFastStartMatchmaking();
        } else {
          this.matchmakingManager.startMatchmaking();
        }
      }
    }
  }

  /**
   * Check environment (embedded browser, etc.)
   */

  /**
   * Blocks entry when the campaign does not support this device class.
   *
   * Peer terminal phase is chosen by `resolveTerminalPhase` with a device gate:
   * - fallback on  → `device_not_supported` (handoff UI)
   * - fallback off → `thank_you` / `auto_redirect` (historical CTA escape hatch)
   *
   * Graph-routed campaigns were never gated and are not gated now — see
   * `checkEnvironment` / #14538.
   *
   * @returns true when the flow was blocked and the caller should stop.
   */
  private applyDeviceLock(): boolean {
    const theme = this.state.currentState.theme;
    if (!theme) {
      return false;
    }

    const { supported, deviceType } = getDeviceSupportStatus(theme);
    if (supported) {
      return false;
    }

    console.log(`[QuickstartCoordinator] Device type "${deviceType}" is not supported by this campaign`);
    this.analyticsManager.sendDeviceNotSupported(deviceType, theme.supportedDevices ?? {});

    const hasResolvedCtaUrl = !!getResolvedCtaUrl(theme);
    const fallbackEnabled = isDeviceFallbackEnabled(
      resolveDeviceFallbackEnabledFor(theme),
      deviceType,
    );
    const phase = resolveTerminalPhase({
      theme,
      hasResolvedCtaUrl,
      deviceGate: { supported: false, fallbackEnabled },
    });

    if (phase === 'device_not_supported') {
      const lockedTo = getDeviceLockTarget(theme);
      this.state.updateState({
        ...this.state.currentState,
        exitReason: 'device_not_supported',
        shouldAutoRedirectToStore: false,
        deviceLock: { detectedDeviceType: deviceType, lockedTo },
      });
      this.transitionTo('device_not_supported');
      this.analyticsManager.sendDeviceFallbackViewed(
        deviceType,
        lockedTo,
        getHandoffTarget(theme.supportedDevices, deviceType),
      );
      return true;
    }

    // Opted out of the handoff screen: same peer terminals as a session exit.
    this.state.updateState({
      ...this.state.currentState,
      exitReason: 'device_not_supported',
      shouldAutoRedirectToStore: phase === 'auto_redirect',
    });
    this.transitionToCTA();
    return true;
  }

  private async checkEnvironment(): Promise<void> {
    this.transitionTo('environment_check');

    // Graph-routed campaigns are left exactly as they were. The device lock has never
    // applied to them — the router branch has always returned before the check — so
    // enforcing it here would not swap one blocked experience for a better one, it would
    // start blocking visitors who play these campaigns today. That is a rollout decision
    // rather than a code one, and the opt-out this screen ships with cannot express it:
    // turning a device off yields a CTA redirect, not the pass-through these campaigns
    // currently get. Gating them is tracked in #14538.
    if (this.router) {
      this.runEnvironmentCheckRouted();
      return;
    }

    if (this.applyDeviceLock()) {
      return;
    }

    // Hardcoded fallback
    this.analyticsManager.sendEnvironmentCheckCompleted();
    const shouldBlock = shouldBlockQuickstart(this.state.currentState.environment);

    if (shouldBlock) {
      this.transitionTo('embedded_browser_blocked');
      this.analyticsManager.sendEmbeddedBrowserBlocked();
    } else if (config.USE_FAST_START) {
      await this.matchmakingManager.startFastStartMatchmaking();
    } else {
      await this.matchmakingManager.startMatchmaking();
    }
  }

  private isTerminalPhase(): boolean {
    return TERMINAL_PHASES.has(this.state.currentState.phase);
  }

  // ========================================
  // Recovery episode budget
  // ========================================

  /** Begin the episode if one is not already running; a retry inherits the first clock. */
  private beginRecoveryEpisode(): void {
    if (this.recoveryEpisodeStartedAt === null) {
      this.recoveryEpisodeStartedAt = Date.now();
    }
  }

  /** Forget the episode, so the next failure is timed from its own start. */
  private endRecoveryEpisode(): void {
    this.recoveryEpisodeStartedAt = null;
  }

  /**
   * True while another in-place attempt still fits inside `GUEST_RECOVERY_HARD_CEILING_MS`.
   *
   * Two clocks are consulted because neither alone is sufficient. PeerCore's episode is the
   * authority while a guest session exists, but a session the app has already closed reports
   * a full budget again — so the coordinator keeps its own clock across the close/rebuild
   * boundary. An `undefined` transport budget means "no session to ask", never "no budget";
   * treating it as exhausted would refuse a rejoin the moment it is most needed.
   */
  private hasRecoveryBudgetLeft(): boolean {
    if (this.recoveryEpisodeStartedAt !== null) {
      const elapsed = Date.now() - this.recoveryEpisodeStartedAt;
      if (elapsed >= GUEST_RECOVERY_HARD_CEILING_MS) return false;
    }
    const transportRemaining = guestRecoveryBudgetRemainingMs();
    return transportRemaining === undefined || transportRemaining > 0;
  }

  /**
   * Terminal outcome for a session whose recovery budget is spent.
   *
   * The CTA is the honest end of the flow once nothing can be re-established; what this
   * replaces is not a better outcome but a Play-Now button over a dead stream.
   */
  private exitAfterRecoveryExhausted(): void {
    this.endRecoveryEpisode();
    this.pendingRejoinReason = null;
    this.isReconnecting = false;
    const shouldAutoRedirectToStore = this.state.currentState.theme?.autoRedirectToCTA === true
      && !!(this.state.currentState.theme ? getResolvedCtaUrl(this.state.currentState.theme) : undefined);
    this.state.updateState({
      ...this.state.currentState,
      exitReason: 'connection_lost',
      isReconnecting: false,
      shouldShowPlayNowOverlay: false,
      shouldAutoRedirectToStore,
    });
    this.timerManager.stopAFKTimer();
    this.timerManager.stopAdTimer();
    this.inputCaptureManager.stop();
    this.disconnectPeer('quickstart:recovery_exhausted');
    this.transitionToCTA();
  }

  /**
   * Handle matchmaking failure
   */
  private onMatchmakingFailed(reason: string): void {
    const duration = this.state.currentState.matchmakingStartTime ? Date.now() - this.state.currentState.matchmakingStartTime : 0;

    // Debug: Log auth check conditions
    console.log('[QuickstartCoordinator] onMatchmakingFailed - Auth check:', {
      requireAuth: this.state.currentState.theme?.requireAuth,
      playerAuthId: this.state.currentState.playerAuthId,
      willRedirectToAuth: this.state.currentState.theme?.requireAuth && !this.state.currentState.playerAuthId,
    });

    // Check if authentication is required before showing error page
    if (this.state.currentState.theme?.requireAuth && !this.state.currentState.playerAuthId) {
      console.log('[QuickstartCoordinator] requireAuth enabled (matchmaking failed), redirecting to auth page');

      this.authFlowManager.startAuthFlow();
      return;
    }

    this.analyticsManager.sendMatchmakingFailed(reason, duration);

    const hasCtaUrl = !!(this.state.currentState.theme && getResolvedCtaUrl(this.state.currentState.theme));
    if (this.state.currentState.theme?.autoRedirectToCTA && hasCtaUrl) {
      this.state.updateState({
        ...this.state.currentState,
        exitReason: 'matchmaking_failed',
        shouldAutoRedirectToStore: true,
        errorMessage: reason,
      });
      this.transitionTo('auto_redirect');
    } else {
      this.transitionTo('matchmaking_failed');
      this.state.updateState({ ...this.state.currentState, errorMessage: reason });
    }
  }

  /**
   * Handle broadcast join failure
   */
  private onBroadcastJoinFailed(reason: string): void {
    if (this.isTerminalPhase()) {
      console.log('[QuickstartCoordinator] Ignoring late onBroadcastJoinFailed (already on terminal phase:', this.state.currentState.phase, ')');
      return;
    }

    const isReconnectAttempt = this.isReconnecting;
    const currentRetries = this.state.currentState.connectionRetries;
    const maxRetries = 2;

    if (isReconnectAttempt) {
      console.log(`[QuickstartCoordinator] Reconnection attempt failed (attempt ${currentRetries}/${maxRetries}): ${reason}`);

      // Check if we should try again
      if (currentRetries < maxRetries) {
        console.log('[QuickstartCoordinator] Will retry reconnection...');
        // attemptReconnect() will increment the counter and try again
        this.attemptReconnect();
        return;
      } else {
        console.log('[QuickstartCoordinator] ❌ All reconnection attempts exhausted, exiting to CTA');
        this.isReconnecting = false;
        const shouldAutoRedirectToStore = resolveAutoRedirect(
          this.state.currentState.theme,
          !!this.state.currentState.theme && !!getResolvedCtaUrl(this.state.currentState.theme),
        );
        this.state.updateState({ ...this.state.currentState, exitReason: 'manual_exit', isReconnecting: false, shouldAutoRedirectToStore });
        this.timerManager.stopAFKTimer();
        this.timerManager.stopAdTimer();
        this.inputCaptureManager.stop();

        // Force close peer connection after failed reconnection attempts
        this.disconnectPeer('quickstart:reconnect_failed');

        this.transitionToCTA();
        return;
      }
    }

    // Initial connection failed (not a reconnection attempt)
    // Debug: Log auth check conditions
    console.log('[QuickstartCoordinator] onBroadcastJoinFailed - Auth check:', {
      requireAuth: this.state.currentState.theme?.requireAuth,
      playerAuthId: this.state.currentState.playerAuthId,
      willRedirectToAuth: this.state.currentState.theme?.requireAuth && !this.state.currentState.playerAuthId,
    });

    // Check if authentication is required before showing error page
    if (this.state.currentState.theme?.requireAuth && !this.state.currentState.playerAuthId) {
      console.log('[QuickstartCoordinator] requireAuth enabled (broadcast join failed), redirecting to auth page');

      this.authFlowManager.startAuthFlow();
      return;
    }

    const hasCtaUrlForRedirect = !!(this.state.currentState.theme && getResolvedCtaUrl(this.state.currentState.theme));
    if (this.state.currentState.theme?.autoRedirectToCTA && hasCtaUrlForRedirect) {
      this.state.updateState({
        ...this.state.currentState,
        exitReason: 'broadcast_join_failed',
        shouldAutoRedirectToStore: true,
        errorMessage: reason,
      });
      this.transitionTo('auto_redirect');
    } else {
      this.transitionTo('broadcast_join_failed');
      this.state.updateState({ ...this.state.currentState, errorMessage: reason });
    }
  }

  /**
   * Handle successful broadcast connection
   */
  private onBroadcastConnected(): void {
    if (this.isCleanedUp) return;
    if (this.isTerminalPhase()) {
      console.log('[QuickstartCoordinator] Ignoring late onBroadcastConnected (already on terminal phase:', this.state.currentState.phase, ')');
      return;
    }

    const isReconnect = this.state.currentState.connectionRetries > 0 || this.isReconnecting;
    const sessionStartTime = Date.now();

    // A live stream ends the episode: a drop minutes from now is a new failure and deserves
    // a full budget, not the remainder of one that already succeeded.
    this.endRecoveryEpisode();
    this.pendingRejoinReason = null;

    // Start AFK timer BEFORE updating state to prevent UI race condition
    this.timerManager.startAFKTimer();

    // Update state with timer active flag
    this.state.updateState({
      ...this.state.currentState,
      sessionStartTime,
      afkTimerActive: true,
      shouldShowPlayNowOverlay: isReconnect ? false : true,
      connectionRetries: 0,
      isReconnecting: false,
    });

    // Clear reconnection flag - we're now connected and ready for new disconnects
    this.isReconnecting = false;

    // Calculate time from connection attempt to game ready
    const timeFromJoinMs = this.state.currentState.connectionStartTime ? sessionStartTime - this.state.currentState.connectionStartTime : 0;

    if (isReconnect) {
      // On reconnect, skip preparing phase — go directly to playing
      this.transitionTo('playing_afk');
      console.log('[QuickstartCoordinator] ✅ Reconnected successfully, resuming session');
      this.inputCaptureManager.start();
    } else {
      // First connection: enter preparing phase so the player component mounts
      // behind the loading overlay before it fades out.
      this.transitionTo('preparing_player');
      this.preparingPlayerTimeout = setTimeout(() => {
        if (this.state.currentState.phase === 'preparing_player') {
          this.transitionTo('playing_afk');
        }
      }, 500);
      console.log('[QuickstartCoordinator] Initial connection successful');
      this.analyticsManager.sendAFKTimerStarted(timeFromJoinMs);
    }

    // Update exit payload with broadcast connection data
    this.sendExitPayloadToAPI();
  }

  /**
   * Handle browser lock changes
   */
  private handleBrowserLockChange(lockType: string, active: boolean): void {
    console.log(`[QuickstartCoordinator] Browser lock change: ${lockType} = ${active}, phase = ${this.state.currentState.phase}`);

    // On mobile, visibility changes (notification shade, brief app switch, system dialog)
    // should NOT trigger the fullscreen exit behavior. Doing so bounces the user to
    // playing_afk and stops idle detection, stranding them without a visual AFK indicator.
    if (lockType === 'visibility' && isMobileDevice()) {
      console.log('[QuickstartCoordinator] Ignoring visibility change on mobile — not a fullscreen exit');
      return;
    }

    // Only trigger on lock RELEASE (not acquire) during active play
    // Note: Either fullscreen OR pointer lock can trigger this (whichever releases first)
    if (!active && this.state.currentState.phase === 'playing_active') {
      console.log(`[QuickstartCoordinator] ${lockType} released during active play - handling fullscreen exit`);

      const theme = this.state.currentState.theme;
      if (!theme) return; // Theme not loaded yet

      const exitBehavior = theme.fullscreenExitBehavior ?? 'return_to_afk_with_overlay';

      switch (exitBehavior) {
        case 'redirect_to_cta':
          // Immediately disconnect and redirect to CTA page
          console.log('[QuickstartCoordinator] Fullscreen exited - redirecting to CTA');
          this.onFullscreenExitToCTA();
          break;

        case 'return_to_afk_with_overlay':
          // Show Play Now overlay again and restart AFK timer
          console.log('[QuickstartCoordinator] Fullscreen exited - returning to AFK with overlay');
          this.onReturnToPlayNow(true); // true = show overlay
          break;

        case 'return_to_afk_silent': {
          // Restart AFK timer without showing overlay
          console.log('[QuickstartCoordinator] Fullscreen exited - returning to AFK silently');
          this.onReturnToPlayNow(false); // false = don't show overlay
          break;
        }

        case 'continue_playing':
          // Keep ad timer running, no interruption
          console.log('[QuickstartCoordinator] Fullscreen exited - continuing with ad timer');
          // Do nothing - player continues playing with ad timer
          break;
      }
    }
  }

  // ========== Public API ==========

  /**
   * Request browser locks (fullscreen, pointer lock, keyboard lock)
   */
  public async requestBrowserLocks(): Promise<void> {
    await this.browserLockManager.requestLocks();
  }

  /**
   * Re-establish the session behind the Play-Now affordance.
   *
   * After a non-deliberate disconnect the flow parks on `playing_afk` with the Play-Now
   * overlay up, but activating it used to only move phases: the transport was already gone,
   * so the user was tapping a button over a dead stream with nothing behind it. This is what
   * gives that control something to do.
   *
   * A no-op unless a rejoin is actually pending, so the happy-path first input and every
   * deliberate end are untouched. Single-shot: the pending reason is consumed on entry, so a
   * second activation cannot stack a second rebuild on the first.
   *
   * @returns true when a rejoin was started.
   */
  public async rejoinAfterConnectionLoss(): Promise<boolean> {
    if (this.isCleanedUp) return false;
    const reason = this.pendingRejoinReason;
    if (!reason) return false;
    this.pendingRejoinReason = null;

    // Deliberately NOT gated on the recovery budget. That budget bounds how long we thrash
    // automatically before giving up; it does not decide whether a person is allowed to ask
    // for a session. This method is reached only from user actions — first input, or the
    // Play-Now control — and refusing one because a clock ran out while nobody was looking
    // rebuilds the exact defect this phase exists to remove: a control that does nothing.
    // rejoinGuestSession starts a fresh episode, which is what a deliberate rejoin deserves.
    console.log('[QuickstartCoordinator] Re-establishing the session:', reason);
    this.isReconnecting = true;
    this.state.updateState({ ...this.state.currentState, isReconnecting: true });
    // The same milestone BroadcastConnectionManager emits when it re-enters a join, so an
    // equivalent recovery has the same funnel shape as beacon's.
    this.analyticsManager.sendBroadcastJoinAttempted();

    const started = await rejoinGuestSession(reason);
    if (started) return true;

    console.warn('[QuickstartCoordinator] Rejoin could not be started');
    this.exitAfterRecoveryExhausted();
    return false;
  }

  /**
   * Handle first input (Play Now button clicked)
   */
  public onFirstInput(inputDeviceType?: 'keyboard' | 'mouse' | 'touch' | 'gamepad'): void {
    // Every route into "the player just asked to play" lands here — the desktop attract
    // overlay, the Play-Now card, and mobile's auto-join — so this is where a pending rejoin
    // is spent. Fire-and-forget: an error handler that blocks the first input would leave the
    // user staring at the same frozen frame the rejoin exists to clear.
    void this.rejoinAfterConnectionLoss();

    const firstInputTime = Date.now();
    const timeToFirstInput = this.state.currentState.sessionStartTime ? firstInputTime - this.state.currentState.sessionStartTime : 0;

    // Stop AFK timer and start Ad timer BEFORE updating state
    // This prevents the UI from polling getAdRemainingTime() before the timer is ready
    this.timerManager.stopAFKTimer();
    this.timerManager.startAdTimer();

    this.state.updateState({
      ...this.state.currentState,
      firstInputTime,
      afkTimerActive: false,
      adTimerActive: true,
      shouldShowPlayNowOverlay: false, // Hide overlay when entering active play
    });

    this.transitionTo('playing_active'); // Transition to active phase (timer already started)

    this.analyticsManager.sendFirstInputDetected(timeToFirstInput, inputDeviceType);
    this.analyticsManager.sendMilestoneFirstInput();
    this.liveOpsManager.emitFirstInput();
    this.analyticsManager.sendAdTimerStarted();
  }

  /**
   * Return to Play Now screen
   * @param showOverlay - Whether to show the Play Now overlay (true) or keep it hidden (false)
   */
  public onReturnToPlayNow(showOverlay = true): void {
    const afkTime = this.state.currentState.sessionStartTime
      ? this.timerManager.calculateAFKTime(this.state.currentState.sessionStartTime)
      : 0;

    // Stop Ad timer and start AFK timer BEFORE updating state
    // This prevents the UI from polling getAFKRemainingTime() before the timer is ready
    this.timerManager.stopAdTimer();
    this.timerManager.startAFKTimer();

    this.state.updateState({
      ...this.state.currentState,
      afkTimerActive: true,
      adTimerActive: false,
      shouldShowPlayNowOverlay: showOverlay,
      cumulativeAfkTime: this.state.currentState.cumulativeAfkTime + afkTime,
      sessionStartTime: Date.now(),
    });

    this.transitionTo('playing_afk'); // Transition to AFK phase (timer already started)
  }

  // ---------------------------------------------------------------------------
  // In-Game Idle public API
  // ---------------------------------------------------------------------------

  /** Reset the in-game idle timer. Call on every user input event. */
  public resetInGameIdleTimer(): void {
    this.inGameIdleManager.resetIdleTimer();
  }

  /** Get remaining seconds for the AFK countdown (UI polling). */
  public getInGameIdleRemainingTime(): number {
    return this.inGameIdleManager.getRemainingTime();
  }

  /** Force the idle state on (for debug overlay). */
  public forceInGameIdle(): void {
    this.inGameIdleManager.forceIdle();
    // Sync effective timeout into state so the UI timer bar knows the correct total
    const effective = this.inGameIdleManager.getEffectiveTimeout();
    if (effective !== this.state.currentState.inGameIdleTimeoutSeconds) {
      this.state.updateState({ ...this.state.currentState, inGameIdleTimeoutSeconds: effective });
    }
  }

  /** Recreate the idle manager with a new timeout (called when theme loads). */
  private applyIdleTimeout(seconds: number): void {
    this.inGameIdleManager.cleanup();
    this.inGameIdleManager = new InGameIdleManager({
      timeoutSeconds: seconds,
      onIdleStart: () => {
        this.state.updateState({ ...this.state.currentState, inGameIdleActive: true });
      },
      onIdleEnd: () => {
        this.state.updateState({ ...this.state.currentState, inGameIdleActive: false });
      },
      onIdleTimeout: () => {
        this.handleIntentionalExit('afk_timeout', 'afk_timeout');
      },
    });

    // If already in playing_active, start monitoring immediately
    if (this.state.currentState.phase === 'playing_active') {
      this.inGameIdleManager.start();
    }
  }

  /**
   * Handle fullscreen exit that should go to CTA
   * (intentional exit, not a disconnect to retry)
   */
  private onFullscreenExitToCTA(): void {
    console.log('[QuickstartCoordinator] Fullscreen exit - intentional disconnect to CTA');
    this.handleIntentionalExit('manual_exit');
  }

  /**
   * Centralized exit handler for all intentional exits
   * (timeout, wishlist click, manual disconnect, fullscreen exit)
   */
  private handleIntentionalExit(
    exitReason: 'afk_timeout' | 'ad_timer_expired' | 'wishlist_clicked' | 'manual_exit',
    transitionPhase?: QuickstartPhase,
  ): void {
    if (this.isCleanedUp) {
      console.warn('[QuickstartCoordinator] Ignoring handleIntentionalExit after cleanup:', exitReason);
      return;
    }
    console.log(`[QuickstartCoordinator] Intentional exit: ${exitReason}`);

    // Calculate metrics based on exit reason
    const afkTime = this.state.currentState.sessionStartTime
      ? this.timerManager.calculateAFKTime(this.state.currentState.sessionStartTime)
      : 0;
    const playtime = this.state.currentState.firstInputTime
      ? this.timerManager.calculatePlaytime(this.state.currentState.firstInputTime)
      : 0;

    // Auto-redirect ONLY if explicitly enabled in theme AND a CTA URL is configured
    const shouldAutoRedirectToStore = resolveAutoRedirect(
      this.state.currentState.theme,
      !!this.state.currentState.theme && !!getResolvedCtaUrl(this.state.currentState.theme),
    );

    // Update state
    this.state.updateState({
      ...this.state.currentState,
      exitReason,
      totalPlaytime: playtime,
      shouldAutoRedirectToStore,
    });

    // Stop all timers and input capture
    this.timerManager.stopAFKTimer();
    this.timerManager.stopAdTimer();
    this.inputCaptureManager.stop();

    // Send analytics based on exit reason
    switch (exitReason) {
      case 'afk_timeout':
        this.analyticsManager.sendAFKTimeoutReached(afkTime);
        break;
      case 'ad_timer_expired':
        this.analyticsManager.sendAdTimerExpired(playtime);
        break;
      case 'wishlist_clicked':
        this.analyticsManager.sendWishlistClicked(playtime);
        break;
    }

    // CRITICAL: Invalidate invitation and disconnect peer
    // This ensures the host knows the session ended and can restart for the next player
    this.matchmakingManager.destroyInvitation();
    this.disconnectPeer(`quickstart:${exitReason}`);

    // Route to final destination (single transition per path)
    const requiresAuth = this.state.currentState.theme?.requireAuth && !this.state.currentState.playerAuthId;

    if (requiresAuth) {
      // Auth required: park on intermediate phase while auth runs.
      // After auth succeeds, onAuthSuccess → transitionToThankYou() handles auto-redirect.
      if (transitionPhase) {
        this.transitionTo(transitionPhase);
      }
      this.authFlowManager.startAuthFlow();
    } else if (shouldAutoRedirectToStore) {
      this.transitionTo('auto_redirect');
    } else {
      this.transitionTo('thank_you');
      this.analyticsManager.sendThankYouPageViewed(this.state.currentState.playerAuthId || 'anonymous');
    }
  }

  public onAfkTimeout(): void {
    this.handleIntentionalExit('afk_timeout', 'afk_timeout');
  }

  public onAdTimerExpired(): void {
    this.handleIntentionalExit('ad_timer_expired', 'ad_timer_expired');
  }

  public onWishlistClicked(): void {
    this.handleIntentionalExit('wishlist_clicked', 'wishlist_early_exit');
  }

  public onLoadingTimeout(): void {
    console.log('[QuickstartCoordinator] Loading timeout reached');

    // Check if auto-redirect is enabled in theme AND a CTA URL is configured
    const hasCtaUrl = !!(this.state.currentState.theme && getResolvedCtaUrl(this.state.currentState.theme));
    const autoRedirectEnabled = resolveAutoRedirect(this.state.currentState.theme, hasCtaUrl);

    if (autoRedirectEnabled) {
      console.log('[QuickstartCoordinator] Auto-redirect enabled - redirecting to CTA');

      this.state.updateState({
        ...this.state.currentState,
        exitReason: 'loading_timeout',
        shouldAutoRedirectToStore: true,
      });

      // Stop timers and clean up
      this.timerManager.stopAFKTimer();
      this.timerManager.stopAdTimer();
      this.inputCaptureManager.stop();
      this.matchmakingManager.destroyInvitation();
      this.disconnectPeer('quickstart:loading_timeout');

      // Transition to auto-redirect page
      this.transitionTo('auto_redirect');
    } else {
      // Manual mode: Overlay will show "taking long" button, no action needed
      console.log('[QuickstartCoordinator] Auto-redirect disabled - overlay will show manual button');

      this.state.updateState({
        ...this.state.currentState,
        exitReason: 'loading_timeout',
      });
    }
  }

  public onPlayerDisconnect(manualDisconnect = false): void {
    // If this is a manual disconnect from UI (e.g., mobile gamepad button), treat as intentional
    if (manualDisconnect) {
      console.log('[QuickstartCoordinator] Manual disconnect from UI');
      this.handleIntentionalExit('manual_exit');
      return;
    }

    // If this is an intentional disconnect (e.g., timer expired), ignore this callback
    // to avoid overriding the already-set exitReason
    if (this.isIntentionalDisconnect) {
      console.log('[QuickstartCoordinator] Ignoring disconnect event (intentional disconnect in progress)');
      return;
    }

    if (this.isTerminalPhase()) {
      console.log('[QuickstartCoordinator] Ignoring late disconnect event (already on terminal phase:', this.state.currentState.phase, ')');
      return;
    }

    const kickReason = playerConnectionState.currentState.kickReason;
    console.log(
      `[QuickstartCoordinator] Player disconnected. Reason: "${kickReason}", PeerCore owns WebRTC retries`,
    );

    this.isReconnecting = false;
    const deliberate = isDeliberateSessionEnd(kickReason);

    if (deliberate) {
      // A deliberate end is terminal in both modes. Clearing any pending rejoin here is what
      // stops a later Play-Now activation from resurrecting a session the host closed.
      this.pendingRejoinReason = null;
      this.endRecoveryEpisode();
      const shouldAutoRedirectToStore = this.state.currentState.theme?.autoRedirectToCTA === true
        && !!(this.state.currentState.theme ? getResolvedCtaUrl(this.state.currentState.theme) : undefined);
      this.state.updateState({
        ...this.state.currentState,
        exitReason: 'session_ended',
        isReconnecting: false,
        shouldAutoRedirectToStore,
      });
      this.timerManager.stopAFKTimer();
      this.timerManager.stopAdTimer();
      this.inputCaptureManager.stop();
      this.matchmakingManager.destroyInvitation();
      this.disconnectPeer('quickstart:session_ended');
      this.transitionToCTA();
      return;
    }

    // Non-deliberate: offer Join / Play Now again instead of a dead-end CTA.
    //
    // Offered regardless of the remaining budget. A spent budget means AUTOMATIC recovery is
    // over, not that the player must be ejected — and the control genuinely re-establishes the
    // session now, so it is no longer the dead end it used to be. Ending the session here
    // would take the decision away from the person best placed to make it.
    this.beginRecoveryEpisode();

    console.log('[QuickstartCoordinator] Non-deliberate disconnect — offering Join again');
    // Arm the affordance. Until this existed the overlay's handler only moved phases, so the
    // user tapped a button over a dead stream and nothing was ever re-established.
    // A reason the transport contract reads as retryable: the deliberate branch has already
    // returned, so what is left is a transient loss by definition, and an unclassifiable raw
    // reason would have the facade refuse the rejoin and reproduce the dead end.
    this.pendingRejoinReason = 'connection_lost:quickstart_play_now';
    this.state.updateState({
      ...this.state.currentState,
      exitReason: undefined,
      isReconnecting: false,
      shouldShowPlayNowOverlay: true,
      connectionRetries: 0,
    });
    this.timerManager.stopAFKTimer();
    this.timerManager.stopAdTimer();
    this.inputCaptureManager.stop();
    this.disconnectPeer('quickstart:connection_lost_rejoinable');
    if (this.state.currentState.phase === 'playing_afk' || this.state.currentState.phase === 'playing_active') {
      this.transitionTo('playing_afk');
    }
  }

  /**
   * Attempt to reconnect to the same broadcast after a disconnect
   */
  private attemptReconnect(): void {
    this.beginRecoveryEpisode();
    if (!this.hasRecoveryBudgetLeft()) {
      // The episode budget is the cap on ALL in-place recovery, not on each attempt. Starting
      // another ladder here is precisely the stacking this phase removes.
      console.log('[QuickstartCoordinator] Recovery budget spent — not starting another attempt');
      this.exitAfterRecoveryExhausted();
      return;
    }

    const state = this.state.currentState;

    // Increment retry counter and signal UI
    this.state.updateState({
      ...state,
      connectionRetries: state.connectionRetries + 1,
      isReconnecting: true,
    });

    // Stop input capture during reconnection
    this.inputCaptureManager.stop();

    // Keep timers running - we don't want to reset the session
    console.log('[QuickstartCoordinator] Keeping timers running during reconnect');

    // Attempt to reconnect to the same broadcast
    if (state.broadcastId) {
      console.log(`[QuickstartCoordinator] Reconnecting to broadcast: ${state.broadcastId} (timeout: ${this.RECONNECT_TIMEOUT_MS}ms)`);
      this.broadcastConnectionManager.connectToBroadcast(this.RECONNECT_TIMEOUT_MS);
    } else {
      console.error('[QuickstartCoordinator] No broadcast ID for reconnect, exiting to CTA');
      this.isReconnecting = false;
      const shouldAutoRedirectToStore = resolveAutoRedirect(
          this.state.currentState.theme,
          !!this.state.currentState.theme && !!getResolvedCtaUrl(this.state.currentState.theme),
        );
      this.state.updateState({ ...state, exitReason: 'manual_exit', isReconnecting: false, shouldAutoRedirectToStore });
      this.timerManager.stopAFKTimer();
      this.timerManager.stopAdTimer();

      // Force close peer connection before exiting
      this.disconnectPeer('quickstart:no_broadcast_id');

      this.transitionToCTA();
    }
  }

  /**
   * Handle invitation invalidation (invitation became invalid during play)
   */
  /**
   * Handle match end reported by the backend (v2 MATCH_END).
   * Tears down the stream and lands on a neutral match-ended screen.
   * Ended ≠ failed — no failure analytics, no error page.
   */
  private onMatchEnded(reason: string): void {
    if (this.isCleanedUp) return;
    if (this.isTerminalPhase()) {
      console.log('[QuickstartCoordinator] Ignoring late onMatchEnded (already on terminal phase:', this.state.currentState.phase, ')');
      return;
    }

    console.log('[QuickstartCoordinator] Match ended - tearing down stream:', reason);
    this.state.updateState({
      ...this.state.currentState,
      exitReason: 'match_ended',
      matchEndReason: reason,
      playerConnectionId: undefined,
    });
    this.timerManager.stopAFKTimer();
    this.timerManager.stopAdTimer();
    this.inputCaptureManager.stop();

    // The request is already over server-side; leave it (no-op if none) and
    // disconnect the peer so the stream actually tears down.
    this.matchmakingManager.destroyInvitation();
    this.disconnectPeer('quickstart:match_ended');

    this.transitionTo('match_ended');
  }

  private onInvitationInvalidated(): void {
    console.log('[QuickstartCoordinator] Invitation invalidated - exiting to CTA');
    const shouldAutoRedirectToStore = resolveAutoRedirect(
      this.state.currentState.theme,
      !!this.state.currentState.theme && !!getResolvedCtaUrl(this.state.currentState.theme),
    );
    this.state.updateState({ ...this.state.currentState, exitReason: 'invitation_invalidated', shouldAutoRedirectToStore });
    this.timerManager.stopAFKTimer();
    this.timerManager.stopAdTimer();
    this.inputCaptureManager.stop();

    // NOTE: Invitation already invalidated by backend - don't call destroyInvitation()
    // to avoid infinite loop. Just disconnect peer and transition.

    // Disconnect peer gracefully before showing CTA
    this.disconnectPeer('quickstart:invitation_invalidated');

    this.transitionToCTA();
  }

  private transitionToCTA(): void {
    // Check if authentication is required (same as transitionToThankYou)
    if (this.state.currentState.theme?.requireAuth && !this.state.currentState.playerAuthId) {
      console.log('[QuickstartCoordinator] requireAuth enabled (CTA flow), redirecting to auth page');
      this.authFlowManager.startAuthFlow();
      return;
    }

    if (this.state.currentState.shouldAutoRedirectToStore) {
      console.log('[QuickstartCoordinator] Auto-redirect enabled (CTA flow), redirecting to store');
      this.transitionTo('auto_redirect');
      return;
    }

    const totalSessionDuration = this.state.currentState.sessionStartTime ? Date.now() - this.state.currentState.sessionStartTime : 0;

    this.transitionTo('cta_page');
    this.analyticsManager.sendCTAPageViewed(totalSessionDuration);
  }

  private transitionToThankYou(): void {
    // Check if authentication is required after demo ends
    if (this.state.currentState.theme?.requireAuth && !this.state.currentState.playerAuthId) {
      console.log('[QuickstartCoordinator] requireAuth enabled, redirecting to auth page');
      this.authFlowManager.startAuthFlow();
      return;
    }

    const hasResolvedCtaUrl = !!(this.state.currentState.theme && getResolvedCtaUrl(this.state.currentState.theme));
    const shouldAutoRedirect = hasResolvedCtaUrl && (
      this.state.currentState.shouldAutoRedirectToStore ||
      (this.state.currentState.theme?.autoRedirectToCTA === true)
    );

    if (shouldAutoRedirect && !this.state.currentState.shouldAutoRedirectToStore) {
      this.state.updateState({
        ...this.state.currentState,
        shouldAutoRedirectToStore: true,
      });
    }

    if (shouldAutoRedirect) {
      console.log('[QuickstartCoordinator] Auto-redirect enabled, showing auto-redirect page');
      this.transitionTo('auto_redirect');
    } else {
      this.transitionTo('thank_you');
      this.analyticsManager.sendThankYouPageViewed(this.state.currentState.playerAuthId || 'anonymous');
    }
  }

  /**
   * Disconnect peer connection gracefully
   * Sets a flag to prevent the disconnect event from overriding our exitReason
   */
  private disconnectPeer(reason: string): void {
    if (this.isCleanedUp) {
      console.warn('[QuickstartCoordinator] Ignoring disconnectPeer after cleanup:', reason);
      return;
    }
    // Debounce: prevent multiple rapid disconnect calls
    const now = Date.now();
    if (now - this.lastDisconnectTime < this.DISCONNECT_DEBOUNCE_MS) {
      console.log(`[QuickstartCoordinator] Disconnect debounced (called ${now - this.lastDisconnectTime}ms after last call)`);
      return;
    }
    this.lastDisconnectTime = now;

    console.log(`[QuickstartCoordinator] Disconnecting peer: ${reason}`);

    // Send "Player Left Broadcast" analytics before closing
    if (this.state.currentState.playerConnectionId && this.state.currentState.theme) {
      this.analyticsManager.sendPlayerLeftBroadcast();
    }

    // Set flag before disconnecting to ignore any subsequent disconnect events
    this.isIntentionalDisconnect = true;
    closePeer(reason);
  }

  public onWishlistCTAClicked(
    triggerSource?: 'cta_page' | 'thank_you' | 'matchmaking_failed' | 'broadcast_join_failed' | 'error' | 'auth_failed' | 'auto_redirect' | 'afk_timer_hit' | 'maxed_out_game_time' | 'loading_timeout'
  ): void {
    // Send analytics before redirecting — use device-resolved URL so analytics reflects actual destination
    const ctaUrl = this.state.currentState.theme ? getResolvedCtaUrl(this.state.currentState.theme) : undefined;
    this.analyticsManager.sendWishlistCTAClicked(
      ctaUrl ?? '',
      triggerSource,
      this.state.currentState.authProvider
    );
  }

  /**
   * Send wishlist reminder email (called from ThankYou component after auth)
   * Note: This is typically called from the component after Clerk auth completes,
   * not directly from coordinator transitions
   */
  public onWishlistEmailSend(playerAuthId: string, email: string, persistentId: string): void {
    console.log('[QuickstartCoordinator] Sending wishlist email:', { playerAuthId, email, persistentId });

    // Get hostAuthId from broadcast connection (if available)
    const hostAuthId = this.state.currentState.broadcastId?.split(':')[0];

    // Get campaignId from the loaded theme (consistent with matchmaking and analytics)
    const campaignId = this.state.currentState.theme?.slug;

    // Only send if we have a campaign ID (theme should always be loaded by Thank You page)
    if (!campaignId) {
      console.warn('[QuickstartCoordinator] Theme not loaded, skipping wishlist email');
      return;
    }

    // TODO: Wishlist email system is outdated — re-enable once updated
    // notifyWishlistDeadDrop(email, persistentId, hostAuthId, campaignId);
    // this.analyticsManager.sendWishlistEmailSent(playerAuthId, email);
  }

  public onDeveloperLogoClicked(url: string): void {
    const authId = authStore.currentState.playerAuthId;
    this.analyticsManager.sendDeveloperLogoClicked(url, 'new_tab', authId);
  }

  public onThankYouRedirect(steamUrl: string): void {
    const authId = authStore.currentState.playerAuthId;
    this.analyticsManager.sendThankYouRedirect(steamUrl, authId);
  }

  public onAuthSuccess(playerAuthId: string, email?: string, persistentId?: string): void {
    if (this.router) {
      this.authFlowManager.onAuthSuccessNoTransition(playerAuthId, email, persistentId);
      if (!this.followEdge('success')) {
        this.transitionToThankYou();
      }
    } else {
      this.authFlowManager.onAuthSuccess(playerAuthId, email, persistentId);
    }
  }

  public onAuthFailure(reason: string): void {
    const hasCtaForAuthFailure = !!(this.state.currentState.theme && getResolvedCtaUrl(this.state.currentState.theme));
    if (this.router) {
      this.authFlowManager.onAuthFailureNoTransition(reason);
      if (!this.followEdge('failure')) {
        this.transitionTo('auth_failed');
        if (this.state.currentState.theme?.autoRedirectToCTA && hasCtaForAuthFailure) {
          this.state.updateState({
            ...this.state.currentState,
            exitReason: 'auth_failed',
            shouldAutoRedirectToStore: true,
          });
          this.transitionTo('auto_redirect');
        }
      }
    } else {
      this.authFlowManager.onAuthFailure(reason);

      if (this.state.currentState.theme?.autoRedirectToCTA && hasCtaForAuthFailure) {
        this.state.updateState({
          ...this.state.currentState,
          exitReason: 'auth_failed',
          shouldAutoRedirectToStore: true,
        });
        this.transitionTo('auto_redirect');
      }
    }
  }

  public onClerkUnavailable(): void {
    console.log('[QuickstartCoordinator] Clerk unavailable, redirecting to CTA');

    // Update state to show CTA page
    this.state.updateState({
      ...this.state.currentState,
      exitReason: 'manual_exit',
    });

    // Send analytics
    this.analyticsManager.sendAuthFailed('clerk', 'clerk_unavailable');

    // Redirect to CTA page instead of showing auth
    this.transitionToCTA();
  }

  public onOpenInBrowserClicked(): void {
    this.analyticsManager.sendOpenInBrowserClicked(this.state.currentState.environment.browserName);
  }

  /**
   * A blocked visitor asked to continue elsewhere — link emailed or copied.
   * QR display does not fire this (no scan signal); Fallback Viewed covers that impression.
   * The matching `Device Handoff Completed` fires on the device they land on.
   */
  public onDeviceHandoffRequested(method: DeviceHandoffMethod): void {
    const detectedDeviceType = this.state.currentState.deviceLock?.detectedDeviceType;
    if (!detectedDeviceType) return;
    this.analyticsManager.sendDeviceHandoffRequested(method, detectedDeviceType);
  }

  public onTrackingPixelFired(trackerUrl: string): void {
    this.analyticsManager.sendTrackingPixelFired(trackerUrl);
  }

  public retryMatchmaking(): void {
    this.matchmakingManager.retry();
  }

  public setDebugMessageCallback(callback: ((message: PlaycastMessage<PlaycastMessageTarget>) => void) | null): void {
    setDebugMessageCallback(callback);
  }

  public setDebugControllerInputCallback(callback: ((inputType: string, eventType: string) => void) | null): void {
    this.debugControllerInputCallback = callback ?? undefined;
  }

  /**
   * Get remaining time for AFK timer
   * Returns actual remaining time synchronized with backend timer
   */
  public getAFKRemainingTime(): number {
    return this.timerManager.getAFKRemainingTime();
  }

  /**
   * Get remaining time for Ad timer
   * Returns actual remaining time synchronized with backend timer
   */
  public getAdRemainingTime(): number {
    return this.timerManager.getAdRemainingTime();
  }

  /**
   * Get current session ID
   * Returns the unique session identifier for this flow run
   */
  public getSessionId(): string {
    return this.state.currentState.sessionId;
  }

  public debugJumpToPhase(phase: QuickstartPhase): void {
    // Special handling for failure phases when requireAuth is enabled
    const failurePhases: QuickstartPhase[] = ['matchmaking_failed', 'broadcast_join_failed'];
    const isFailurePhase = failurePhases.includes(phase);

    if (isFailurePhase && this.state.currentState.theme?.requireAuth && !this.state.currentState.playerAuthId) {
      console.log('[QuickstartCoordinator] Debug: Jumping to failure phase with requireAuth enabled - redirecting to auth');

      // Redirect to auth
      this.authFlowManager.startAuthFlow();
      return;
    }

    this.transitionTo(phase);
  }

  /**
   * Debug method to override theme's release date timestamp
   * @param timestampOverride - Unix timestamp (ms) to set as release date, or null to use past/future for testing
   */
  public debugSetReleaseTimestamp(timestampOverride: number | 'past' | 'future'): void {
    let timestamp: number;

    if (timestampOverride === 'past') {
      // Set to 1 year ago to simulate released game
      timestamp = Date.now() - 365 * 24 * 60 * 60 * 1000;
    } else if (timestampOverride === 'future') {
      // Set to 1 year from now to simulate unreleased game
      timestamp = Date.now() + 365 * 24 * 60 * 60 * 1000;
    } else {
      timestamp = timestampOverride;
    }

    const currentTheme = this.state.currentState.theme;
    if (!currentTheme) return; // Theme not loaded yet

    this.state.updateState({
      ...this.state.currentState,
      theme: {
        ...currentTheme,
        releaseDateTimestamp: timestamp,
      },
    });
  }

  /**
   * Initialize exit detection system
   *
   * Uses industry best practices:
   * - sendBeacon as primary method (most reliable for page unload)
   * - Pre-warms keepalive probe during init (not during exit)
   * - Fully synchronous exit event firing (no async/await during unload)
   * - Dual delivery: direct sendBeacon + fallback through analytics manager
   *
   * NOTE: Exit events are inherently unreliable (~40-60% success rate is normal)
   * This is primarily for debugging, not business-critical metrics.
   */
  private initializeExitDetection(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const getPlayerIdentifiers = () => {
      const state = this.state.currentState;
      return {
        persistentId: this.frozenPersistentId ?? getPersistentPlayerId(),
        playerAuthId: state.playerAuthId || authStore.currentState.playerAuthId || undefined,
        playerConnectionId: state.playerConnectionId,
        broadcastSessionId: state.broadcastId,
        invitationId: state.matchId,
        flow: 'quickstart' as const,
        campaignId: state.theme?.slug || 'unknown',
      };
    };

    const getSessionStartTime = () => {
      return this.state.currentState.sessionStartTime;
    };

    const getPageLoadTime = () => {
      return this.state.currentState.pageLoadTime;
    };

    const onExit = (reason: ExitReason, metadata: ExitMetadata) => {
      // Fallback: Also send through normal analytics flow for reliability
      // This ensures the event is sent even if sendBeacon/fetch fails
      console.log('[QuickstartCoordinator] Exit detected (fallback):', reason, metadata);
      this.analyticsManager.sendPlayerExited(reason, metadata.sessionDuration, metadata.totalSessionDuration);
    };

    const onVisibilityChange = (reason: VisibilityReason, metadata: ExitMetadata) => {
      // Send tab visibility change event (separate from player exit)
      console.log('[QuickstartCoordinator] Tab visibility changed:', reason, metadata);
      this.analyticsManager.sendTabVisibilityChanged(reason, metadata.sessionDuration, metadata.totalSessionDuration);
    };

    this.exitDetectionCleanup = initializeExitDetection({
      getPlayerIdentifiers,
      getSessionStartTime,
      getPageLoadTime,
      onExit,
      onVisibilityChange,
    });

    console.log('[QuickstartCoordinator] Exit detection initialized with improved reliability');
  }

  /**
   * Send/update player exit payload to API for websocket-based exit detection
   * This payload will be used to fire a player exit event when the websocket disconnects
   */
  private sendExitPayloadToAPI(): void {
    const state = this.state.currentState;

    // Build payload with current state
    const payload = {
      persistentId: this.frozenPersistentId ?? getPersistentPlayerId(),
      playerAuthId: state.playerAuthId || authStore.currentState.playerAuthId || undefined,
      playerConnectionId: state.playerConnectionId,
      broadcastSessionId: state.broadcastId,
      invitationId: state.matchId,
      sessionId: state.sessionId, // Quickstart session ID
      flow: 'quickstart' as const,
      quickstartSlug: state.theme?.slug,
      currentPhase: state.phase, // Track last phase for analytics
      sessionStartTime: state.sessionStartTime,
      pageLoadTime: state.pageLoadTime,
    };

    // Send to API
    try {
      const message = websocket.generators.analytics(
        AnalyticsMessageType.ANALYTICS_STORE_EXIT_PAYLOAD,
        payload
      );
      sendMessage(message);

      console.log('[QuickstartCoordinator] Sent exit payload to API:', {
        currentPhase: payload.currentPhase,
        hasPlayerConnectionId: !!payload.playerConnectionId,
        hasBroadcastId: !!payload.broadcastSessionId,
      });
    } catch (error) {
      console.error('[QuickstartCoordinator] Failed to send exit payload:', error);
      // Don't throw - browser-based detection will be the backup
    }
  }

  /**
   * Cleanup all managers
   */
  public cleanup(): void {
    if (this.isCleanedUp) return;

    console.log('[QuickstartCoordinator] Cleaning up...');
    this.isIntentionalDisconnect = false;
    this.isReconnecting = false;
    this.lastDisconnectTime = 0;

    // Clear OAuth sessionId if it exists (safety cleanup)
    sessionStorage.removeItem('quickstart_session_id');
    console.log('[QuickstartCoordinator] Cleared OAuth sessionId from sessionStorage');

    if (this.preparingPlayerTimeout) {
      clearTimeout(this.preparingPlayerTimeout);
      this.preparingPlayerTimeout = undefined;
    }

    // Cleanup exit detection
    if (this.exitDetectionCleanup) {
      this.exitDetectionCleanup();
      this.exitDetectionCleanup = undefined;
    }

    // Stop reacting to host-driven input access changes
    if (this.captureAccessUnsubscribe) {
      this.captureAccessUnsubscribe();
      this.captureAccessUnsubscribe = undefined;
    }

    // CRITICAL: Disconnect peer connection before cleaning up managers
    // This ensures the host detects playerCount drop to 0 and can restart properly
    this.disconnectPeer('quickstart:cleanup');

    this.isCleanedUp = true;

    this.inputCaptureManager.cleanup();
    this.inGameIdleManager.cleanup();
    this.browserLockManager.cleanup();
    this.timerManager.cleanup();
    this.matchmakingManager.cleanup();
    this.broadcastConnectionManager.cleanup();
    this.authFlowManager.cleanup();
    this.analyticsManager.cleanup();
    super.cleanup();
  }
}

/**
 * Resolve which matchmaking dialect Quickstart should speak.
 * Absent/undefined defaults to v1 (schema + matchmaking-v2.md Phase 6).
 */
export function resolveMatchmakingDialectVersion(version?: 'v1' | 'v2'): 'v1' | 'v2' {
  return version ?? 'v1';
}

// Export for backwards compatibility
export { QuickstartCoordinator as QuickstartController };

